import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Search,
  Filter,
  Download,
  Upload,
  Plus,
  Eye,
  Edit3,
  Copy,
  MoreVertical,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  X,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { useGetInvoices } from "./useGetInvoices";
import { dataTagSymbol } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import AuditTrailModal from "./components/AuditTrailModal";
import { useNavigate } from "react-router-dom";
import StatusBadge from "./components/StatusBadge";
import SortableHeader from "./components/SortableHeader";
import BulkActionsDropdown from "./components/BulkActionsDropdown";
import FloatingActionButton from "./components/FloatingActionButton";
import ActionMenuItem from "./components/ActionMenuItem";
import ActionDropdown from "./components/ActionDropdown";
import InvoiceRow from "./components/InvoiceRow";
import TemplateSelectionModal from "./components/TemplateSelectionModal";

const InvoiceManagementContent = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const { data: invoicesData, isLoading, isError } = useGetInvoices();

  const invoices = useMemo(
    () => (Array.isArray(invoicesData) ? invoicesData : []),
    [invoicesData],
  );

  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [activePaymentFilter, setActivePaymentFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const { isCollapsed } = useSidebar();

  const filterTabs = [
    { key: "all", label: t("common.all"), count: invoices.length },
    {
      key: "DRAFT",
      label: t("status.draft"),
      count: invoices.filter((inv) => inv.status === "DRAFT").length,
    },
    {
      key: "ISSUED",
      label: t("status.issued"),
      count: invoices.filter((inv) => inv.status === "ISSUED").length,
    },
    {
      key: "REPORTED",
      label: t("status.reported"),
      count: invoices.filter((inv) => inv.status === "REPORTED").length,
    },
    {
      key: "CLEARED",
      label: t("status.cleared"),
      count: invoices.filter((inv) => inv.status === "CLEARED").length,
    },
    {
      key: "REJECTED",
      label: t("status.rejected"),
      count: invoices.filter((inv) => inv.status === "REJECTED").length,
    },
  ];

  const paymentFilterTabs = [
    { key: "all", label: t("status.allPayments"), count: invoices.length },
    {
      key: "UNPAID",
      label: t("status.unpaid"),
      count: invoices.filter((inv) => inv.paymentStatus === "UNPAID").length,
    },
    {
      key: "PARTIAL",
      label: t("status.partial"),
      count: invoices.filter((inv) => inv.paymentStatus === "PARTIAL").length,
    },
    {
      key: "PAID",
      label: t("status.paid"),
      count: invoices.filter((inv) => inv.paymentStatus === "PAID").length,
    },
    {
      key: "OVERDUE",
      label: t("status.overdue"),
      count: invoices.filter((inv) => inv.paymentStatus === "OVERDUE").length,
    },
  ];

  useEffect(() => {
    let filtered = invoices;

    if (activeFilter !== "all") {
      filtered = filtered.filter((invoice) => invoice.status === activeFilter);
    }

    if (activePaymentFilter !== "all") {
      filtered = filtered.filter(
        (invoice) => invoice.paymentStatus === activePaymentFilter,
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (invoice) =>
          invoice.invoiceNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          invoice.buyer?.registrationName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          invoice.buyer?.vatNumber?.includes(searchTerm) ||
          invoice.seller?.registrationName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          invoice.amount?.toString().includes(searchTerm),
      );
    }

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        if (sortConfig.key === "amount") {
          const aAmount = parseFloat(a.amount) || 0;
          const bAmount = parseFloat(b.amount) || 0;
          return sortConfig.direction === "asc"
            ? aAmount - bAmount
            : bAmount - aAmount;
        }

        if (sortConfig.key === "date") {
          return sortConfig.direction === "asc"
            ? new Date(a.issuedAt) - new Date(b.issuedAt)
            : new Date(b.issuedAt) - new Date(a.issuedAt);
        }

        if (sortConfig.key === "customer") {
          const aValue = a.buyer?.registrationName?.toLowerCase() || "";
          const bValue = b.buyer?.registrationName?.toLowerCase() || "";

          if (sortConfig.direction === "asc") {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          }

          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }

        if (sortConfig.key === "id") {
          const aValue = a.invoiceNumber?.toLowerCase() || "";
          const bValue = b.invoiceNumber?.toLowerCase() || "";

          if (sortConfig.direction === "asc") {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          }

          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }

        const aValue = a[sortConfig.key]?.toString().toLowerCase() || "";
        const bValue = b[sortConfig.key]?.toString().toLowerCase() || "";

        if (sortConfig.direction === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        }

        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      });
    }

    setFilteredInvoices(filtered);
    setCurrentPage(1);
  }, [activeFilter, activePaymentFilter, searchTerm, sortConfig, invoices]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId],
    );
  };

  const handleCreateInvoice = () => {
    navigate("/invoice-creation-wizard");
  };

  const handleSelectAll = () => {
    const currentPageInvoices = getCurrentPageInvoices().map((inv) => inv.id);
    if (selectedInvoices.length === currentPageInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(currentPageInvoices);
    }
  };

  const getCurrentPageInvoices = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredInvoices.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  if (isLoading) {
    return <LoadingContent isCollapsed={isCollapsed} />;
  }

  if (isError) {
    return (
      <div
        className={`transition-all duration-300 ease-in-out ${
          isCollapsed ? "md:ms-16" : "md:ms-64"
        } p-8`}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Icon
              name="AlertTriangle"
              size={48}
              className="mx-auto text-red-500 mb-4"
            />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t("invoices.failedLoad")}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t("invoices.failedLoadDescription")}
            </p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t("actions.retry")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("invoices.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("invoices.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                iconName="Download"
                className="shadow-sm hover:shadow-lg transition-all duration-300"
              >
                {t("actions.export")}
              </Button>
              <Button
                variant="outline"
                size="md"
                iconName="Upload"
                className="shadow-sm hover:shadow-lg transition-all duration-300"
              >
                {t("actions.upload")}
              </Button>
              <Button
                variant="default"
                size="md"
                iconName="Plus"
                className="shadow-sm hover:shadow-lg transition-all duration-300"
                onClick={handleCreateInvoice}
              >
                {t("actions.createInvoice")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <div className="flex flex-wrap gap-2">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeFilter === tab.key
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-card text-muted-foreground hover:bg-muted border border-border"
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
                      activeFilter === tab.key
                        ? "bg-white text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {paymentFilterTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActivePaymentFilter(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activePaymentFilter === tab.key
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-card text-muted-foreground hover:bg-muted border border-border"
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
                      activePaymentFilter === tab.key
                        ? "bg-white text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Icon
                name="Search"
                size={20}
                className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder={t("invoices.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full ps-10 pe-4 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="md"
                iconName="Filter"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "bg-primary/10 border-primary" : ""}
              >
                {t("actions.filters")}
              </Button>
              {selectedInvoices.length > 0 && (
                <BulkActionsDropdown selectedCount={selectedInvoices.length} />
              )}
            </div>
          </div>

          {/* Advanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-4 pt-4 border-t border-border"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t("invoices.dateRange")}
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t("invoices.amountRange")}
                    </label>
                    <select className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground">
                      <option>{t("status.allAmounts")}</option>
                      <option>Under SR 50,000</option>
                      <option>SR 50,000 - SR 100,000</option>
                      <option>Over SR 100,000</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      {t("invoices.invoiceType")}
                    </label>
                    <select className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground">
                      <option>{t("status.allTypes")}</option>
                      <option>B2B</option>
                      <option>B2C</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Invoice Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-card rounded-xl shadow-sm border border-border overflow-visible"
        >
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-4 text-start">
                    <input
                      type="checkbox"
                      checked={
                        selectedInvoices.length ===
                          getCurrentPageInvoices().length &&
                        getCurrentPageInvoices().length > 0
                      }
                      onChange={handleSelectAll}
                      className="rounded border-border"
                    />
                  </th>
                  <th className="px-6 py-4 text-start">
                    <SortableHeader
                      label={t("invoices.table.invoiceId")}
                      sortKey="id"
                      currentSort={sortConfig}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-4 text-start">
                    <SortableHeader
                      label={t("invoices.table.customer")}
                      sortKey="customer"
                      currentSort={sortConfig}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-4 text-start">
                    <SortableHeader
                      label={t("invoices.table.date")}
                      sortKey="date"
                      currentSort={sortConfig}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-4 text-start">
                    <SortableHeader
                      label={t("invoices.table.amount")}
                      sortKey="amount"
                      currentSort={sortConfig}
                      onSort={handleSort}
                    />
                  </th>
                  <th className="px-6 py-4 text-start">
                    <span className="text-sm font-medium text-foreground">
                      {t("invoices.table.payment")}
                    </span>
                  </th>
                  <th className="px-6 py-4 text-start">
                    <span className="text-sm font-medium text-foreground">
                      {t("invoices.table.status")}
                    </span>
                  </th>
                  <th className="px-6 py-4 text-start">
                    <span className="text-sm font-medium text-foreground">
                      {t("invoices.table.actions")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {getCurrentPageInvoices().map((invoice, index) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    isSelected={selectedInvoices.includes(invoice.id)}
                    onSelect={handleSelectInvoice}
                    index={index}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {t("common.showingRange", {
                    from: (currentPage - 1) * itemsPerPage + 1,
                    to: Math.min(currentPage * itemsPerPage, filteredInvoices.length),
                    total: filteredInvoices.length,
                    entity: t("entities.invoices"),
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronLeft"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  />
                  <span className="px-3 py-1 text-sm text-foreground">
                    {t("common.pageOf", { current: currentPage, total: totalPages })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ChevronRight"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Empty State */}
        {filteredInvoices.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border"
          >
            <Icon
              name="FileText"
              size={48}
              className="mx-auto mb-4 text-muted-foreground"
            />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("invoices.emptyTitle")}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm ||
              activeFilter !== "all" ||
              activePaymentFilter !== "all"
                ? t("common.adjustFiltersOrSearch")
                : t("invoices.createFirst")}
            </p>
            <Button
              variant="default"
              iconName="Plus"
              onClick={handleCreateInvoice}
            >
              {t("actions.createInvoice")}
            </Button>
          </motion.div>
        )}

        {/* Floating Action Button */}
        <FloatingActionButton onCreateInvoice={handleCreateInvoice} />
      </main>
    </div>
  );
};

const InvoiceManagementHub = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <InvoiceManagementContent />
      </div>
    </SidebarProvider>
  );
};

// Loading Content Component
const LoadingContent = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ease-in-out ${
      isCollapsed ? "md:ms-16" : "md:ms-64"
    }`}
  >
    <main className="p-4 md:p-6">
      <div className="animate-pulse">
        {/* Header Skeleton */}
        <div className="mb-8">
          <div className="h-8 bg-muted rounded w-64 mb-2"></div>
          <div className="h-4 bg-muted rounded w-96"></div>
        </div>

        {/* Filter Tabs Skeleton */}
        <div className="flex gap-2 mb-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-10 bg-muted rounded w-20"></div>
          ))}
        </div>

        {/* Search Bar Skeleton */}
        <div className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6">
          <div className="h-12 bg-muted rounded"></div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-card rounded-xl shadow-sm border border-border">
          <div className="p-6">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded mb-2"></div>
            ))}
          </div>
        </div>
      </div>
    </main>
  </div>
);

export default InvoiceManagementHub;
